import { Card, CardBody, Stack, Text } from '@chakra-ui/react';
import keyBy from 'lodash/keyBy';
import { useEffect, useMemo, useState } from 'react';

import { AlgoAction, type OptimizationMeta, TradePlanDetailType } from '@/cip/_openapi/portfolio-optimizer';
import { useOptimizerDetails } from '@/cip/api/optimization';
import { PageSpinner } from '@/cip/widgets/PageSpinner';
import { SegmentedControls } from '@/cip/widgets/SegmentedControls';
import { getAllocationTypeLabel } from 'utils';

import {
    ConvictionAllocationType,
    GrowthAllocationType,
    SecuredAllocationType,
} from '../../../_openapi/portfolio-optimizer';
import { getPortfolioLevelGroupGuid } from './accountGroupAllocation';
import { AccountGroupMembers } from './AccountGroupMembers';
import { BondLadderDetails } from './BondLadderDetails';
import { ConvictionDetails } from './ConvictionDetails';
import { CustomAssetDetails } from './CustomAssetDetails';
import { DirectStocksBuyDetails } from './DirectStocksBuyDetails';
import { DirectStocksSellDetails } from './DirectStocksSellDetails';
import { GlobalBetaBuyDetails } from './GlobalBetaBuyDetails';
import { GlobalBetaSellDetails } from './GlobalBetaSellDetails';
import { AccountAllocationTables } from './OptimizerAllocationTables';
import { PaydayDetails } from './PaydayDetails';
import { type TradeDetailRecord } from './tradeDetailsModel';

const ALLOCATIONS_TAB = 'allocations';

interface AlgoDetailsViewProps {
    optimizationId: number;
    pId: string;
    optimizationMeta?: OptimizationMeta;
}

export const AlgoDetailsView = ({ optimizationId, pId, optimizationMeta }: AlgoDetailsViewProps) => {
    const [selectedTab, setSelectedTab] = useState<string>(ALLOCATIONS_TAB);

    useEffect(() => {
        setSelectedTab(ALLOCATIONS_TAB);
    }, [pId]);

    const { data: optimizerDetails, isPending } = useOptimizerDetails({ optimizationId, pId });

    // The rest of the group is listed for accounts whose growth allocation was configured at portfolio level
    const portfolioLevelGroupGuid = useMemo(
        () => getPortfolioLevelGroupGuid(optimizerDetails ?? []),
        [optimizerDetails],
    );

    const allocationTypeTabs = useMemo(() => {
        if (!optimizerDetails) return [];

        const seen = new Set<string>();
        const result: { value: string; label: string }[] = [];

        for (const detail of optimizerDetails) {
            if (detail.allocationType === null || detail.allocationType === undefined) continue;
            const key = detail.allocationType;
            if (!seen.has(key)) {
                seen.add(key);
                result.push({
                    value: key,
                    label: getAllocationTypeLabel(key),
                });
            }
        }

        result.sort((a, b) => a.label.localeCompare(b.label));

        return result;
    }, [optimizerDetails]);

    const tradePlanDetails = useMemo(() => {
        if (!optimizerDetails || selectedTab === ALLOCATIONS_TAB) return undefined;

        const filtered = optimizerDetails.filter((d) => d.allocationType === selectedTab);

        return keyBy(filtered, (d) => d.detailType) as Partial<Record<TradePlanDetailType, TradeDetailRecord>>;
    }, [optimizerDetails, selectedTab]);

    const tabs = [{ value: ALLOCATIONS_TAB, label: 'Allocations' }, ...allocationTypeTabs];

    return (
        <Stack gap={4}>
            <Card size="sm">
                <CardBody>
                    {isPending ? (
                        <PageSpinner />
                    ) : (
                        <SegmentedControls onClick={setSelectedTab} selectedValue={selectedTab} options={tabs} />
                    )}
                </CardBody>
            </Card>

            {!isPending && (
                <Card size="sm">
                    <CardBody>
                        {selectedTab === ALLOCATIONS_TAB ? (
                            <Stack gap={6}>
                                <AccountAllocationTables details={optimizerDetails ?? []} />
                                {portfolioLevelGroupGuid && (
                                    <AccountGroupMembers
                                        optimizationId={optimizationId}
                                        accountGroupGuid={portfolioLevelGroupGuid}
                                        pId={pId}
                                    />
                                )}
                            </Stack>
                        ) : !tradePlanDetails || Object.keys(tradePlanDetails).length === 0 ? (
                            <Text>Details not present for selected portfolio</Text>
                        ) : (
                            <AllocationDetails
                                optimizationId={optimizationId}
                                pId={pId}
                                allocationType={selectedTab}
                                tradePlanDetails={tradePlanDetails}
                                algoAction={optimizationMeta?.optimizationRequest?.algoAction}
                            />
                        )}
                    </CardBody>
                </Card>
            )}
        </Stack>
    );
};

const GLOBAL_BETA_ALLOCATION_TYPES: string[] = [
    GrowthAllocationType.US_EQUITY_ETF,
    GrowthAllocationType.DEVELOPED_EQUITY_ETF,
    GrowthAllocationType.EMERGING_EQUITY_ETF,
    GrowthAllocationType.LEVERED_BOND_ETF,
    GrowthAllocationType.CASH_EQUIVALENTS,
];

const BOND_LADDER_ALLOCATION_TYPES: string[] = [
    GrowthAllocationType.SHORT_TERM_TREASURIES_3M,
    GrowthAllocationType.SHORT_TERM_TREASURIES_1Y,
    GrowthAllocationType.BOND_LADDER_2Y,
    GrowthAllocationType.BOND_LADDER_5Y,
    GrowthAllocationType.BOND_LADDER_7Y,
    GrowthAllocationType.BOND_LADDER_10Y,
    GrowthAllocationType.BOND_LADDER_20Y,
    GrowthAllocationType.BOND_LADDER_30Y,
];

const AllocationDetails = ({
    optimizationId,
    pId,
    allocationType,
    tradePlanDetails,
    algoAction,
}: {
    optimizationId: number;
    pId: string;
    allocationType: string;
    tradePlanDetails: Partial<Record<TradePlanDetailType, TradeDetailRecord>>;
    algoAction?: AlgoAction;
}) => {
    if (allocationType === SecuredAllocationType.PAYDAY_BOND) {
        return <PaydayDetails tradePlanDetails={tradePlanDetails} />;
    }
    if (allocationType === ConvictionAllocationType.CONVICTION) {
        return <ConvictionDetails tradePlanDetails={tradePlanDetails} />;
    }
    if (allocationType === GrowthAllocationType.US_DIRECT_STOCKS) {
        return algoAction === AlgoAction.BUY ? (
            <DirectStocksBuyDetails tradePlanDetails={tradePlanDetails} />
        ) : (
            <DirectStocksSellDetails tradePlanDetails={tradePlanDetails} />
        );
    }
    if (GLOBAL_BETA_ALLOCATION_TYPES.includes(allocationType)) {
        return algoAction === AlgoAction.BUY ? (
            <GlobalBetaBuyDetails tradePlanDetails={tradePlanDetails} />
        ) : (
            <GlobalBetaSellDetails tradePlanDetails={tradePlanDetails} />
        );
    }
    if (BOND_LADDER_ALLOCATION_TYPES.includes(allocationType)) {
        return <BondLadderDetails tradePlanDetails={tradePlanDetails} />;
    }
    if (allocationType === GrowthAllocationType.CUSTOM_ASSET) {
        return <CustomAssetDetails optimizationId={optimizationId} pId={pId} tradePlanDetails={tradePlanDetails} />;
    }
    return <Text>Details not yet implemented for allocation type: {allocationType}</Text>;
};
